import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { getDappHomePath } from '../../utils/dappRoutes';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { useDappContext } from '../DappLayout';
import { LoadingIcon } from '../LoadingIcon';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface NewVersionPageHeaderProps {
  isFormTouched: boolean;
  isFormValid: boolean;
  isSubmitting?: boolean;
  hasError?: boolean;
  isSubmitDisabled?: boolean;
  disabledTooltip?: string;
}

export function NewVersionPageHeader({
  isFormTouched,
  isFormValid,
  isSubmitting = false,
  hasError = false,
  isSubmitDisabled,
  disabledTooltip,
}: NewVersionPageHeaderProps) {
  const navigate = useNavigate();
  const { dappId, packageName } = useDappContext();
  const homePath = getDappHomePath({ id: dappId, packageName });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const submitDisabled =
    typeof isSubmitDisabled === 'boolean'
      ? isSubmitDisabled
      : !isFormValid || isSubmitting || hasError;

  const handleClose = () => {
    if (isFormTouched) {
      setShowConfirmDialog(true);
    } else {
      navigate(homePath);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    navigate(homePath);
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <div className="p-6 bg-neutral-800 flex justify-between items-center text-white">
        <button
          type="button"
          className="text-white p-2 rounded-full hover:bg-neutral-700"
          onClick={handleClose}
        >
          <CloseIcon className="fill-white" />
        </button>
        <div className="text-xl font-semibold tracking-tight whitespace-nowrap">
          Solana dApp Store
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  type="submit"
                  form="version-form"
                  disabled={submitDisabled}
                  className={`
                    bg-teal-600 text-white font-medium py-2 px-6 rounded-lg
                    disabled:opacity-50 disabled:hover:bg-teal-600
                    ${!submitDisabled ? 'hover:bg-teal-700' : ''}
                    relative min-w-[90px] flex items-center justify-center
                    transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
                  `}
                  style={{
                    cursor: submitDisabled ? 'default' : 'pointer',
                  }}
                >
                  {isSubmitting ? (
                    <LoadingIcon
                      label="Submitting"
                      className="size-5 text-white"
                    />
                  ) : (
                    'Submit'
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {disabledTooltip ? (
              <TooltipContent>
                <p>{disabledTooltip}</p>
              </TooltipContent>
            ) : null}
          </Tooltip>
        </TooltipProvider>
      </div>

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmText="Leave"
        cancelText="Stay"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}
